import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle } from 'lucide-react';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { ConnectionErrorAlert } from '@/pages/admin/bikes/ConnectionErrorAlert';
import CourierActivityProgressBar from './courier-analytics/CourierActivityProgressBar';
import CourierActivityBadge from './courier-analytics/CourierActivityBadge';
import CourierMetricsCard from './courier-analytics/CourierMetricsCard';
import CourierActivityFilters from './courier-analytics/CourierActivityFilters';

interface CourierPaymentStats {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  totalPayments: number;
  repeatedPayments: number;
  multiPayments: number;
  activityPercentage: number;
}

const CourierAnalytics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [dataSource, setDataSource] = useState<'supabase' | 'mock'>('supabase');
  const [couriersStats, setCouriersStats] = useState<CourierPaymentStats[]>([]);
  
  // Фильтры
  const [searchTerm, setSearchTerm] = useState('');
  const [activityFilter, setActivityFilter] = useState('all');
  const [paymentsFilter, setPaymentsFilter] = useState('all');

  useEffect(() => {
    fetchCouriersPaymentStats();
  }, []);

  const fetchCouriersPaymentStats = async () => {
    try {
      setLoading(true);
      
      const { data: couriersData, error: couriersError } = await supabase
        .from('couriers')
        .select('id, name, email, phone')
        .neq('name', 'Не Задан');
          
      if (couriersError) {
        console.error("Ошибка при получении списка курьеров:", couriersError);
        setConnectionError(`Не удалось получить данные курьеров: ${couriersError.message}`);
        setDataSource('mock');
        setLoading(false);
        return;
      }
      
      if (!couriersData || couriersData.length === 0) {
        console.log("Список курьеров пуст");
        setCouriersStats([]);
        setLoading(false);
        return;
      }
      
      const courierStatsPromises = couriersData.map(async (courier) => {
        const { data: paymentsData, error: paymentsError } = await supabase
          .from('payments')
          .select('*')
          .eq('courier_id', courier.id);
            
        if (paymentsError) {
          console.error(`Ошибка при получении платежей для курьера ${courier.id}:`, paymentsError);
          return null;
        }
        
        const totalPayments = paymentsData ? paymentsData.length : 0;
        const paymentsByMonth: Record<string, number> = {};
        
        if (paymentsData) {
          paymentsData.forEach(payment => {
            const paymentDate = payment.payment_date || payment.created_at;
            if (paymentDate) {
              const monthKey = new Date(paymentDate).toISOString().substring(0, 7);
              paymentsByMonth[monthKey] = (paymentsByMonth[monthKey] || 0) + 1;
            }
          });
        }
        
        const repeatedPayments = Object.values(paymentsByMonth).filter(count => count >= 2).length;
        const multiPayments = Object.values(paymentsByMonth).filter(count => count >= 3).length;
        const activityPercentage = totalPayments > 0 ? (repeatedPayments / 12) * 100 : 0;
        
        return {
          id: courier.id,
          name: courier.name,
          email: courier.email,
          phone: courier.phone,
          totalPayments,
          repeatedPayments,
          multiPayments,
          activityPercentage: Math.min(activityPercentage, 100)
        };
      });
      
      const resolvedStats = (await Promise.all(courierStatsPromises)).filter(Boolean) as CourierPaymentStats[];
      const sortedStats = resolvedStats.sort((a, b) => b.repeatedPayments - a.repeatedPayments);
      
      setCouriersStats(sortedStats);
      setDataSource('supabase');
      setLoading(false);
    } catch (err: any) {
      console.error("Ошибка при анализе данных курьеров:", err);
      setError(err.message || "Произошла ошибка при загрузке аналитики курьеров");
      setDataSource('mock');
      setLoading(false);
    }
  };

  // Функция для определения цвета строки и текста
  const getRowStyles = (repeatedPayments: number) => {
    if (repeatedPayments >= 5) {
      return {
        bgClass: 'bg-emerald-100 hover:bg-emerald-150 dark:bg-emerald-900/30 dark:hover:bg-emerald-900/40',
        textClass: 'text-emerald-900 dark:text-emerald-100'
      };
    }
    if (repeatedPayments >= 4) {
      return {
        bgClass: 'bg-green-100 hover:bg-green-150 dark:bg-green-900/30 dark:hover:bg-green-900/40',
        textClass: 'text-green-900 dark:text-green-100'
      };
    }
    if (repeatedPayments >= 2) {
      return {
        bgClass: 'bg-yellow-100 hover:bg-yellow-150 dark:bg-yellow-900/30 dark:hover:bg-yellow-900/40',
        textClass: 'text-yellow-900 dark:text-yellow-100'
      };
    }
    if (repeatedPayments >= 1) {
      return {
        bgClass: 'bg-orange-100 hover:bg-orange-150 dark:bg-orange-900/30 dark:hover:bg-orange-900/40',
        textClass: 'text-orange-900 dark:text-orange-100'
      };
    }
    return {
      bgClass: 'bg-red-100 hover:bg-red-150 dark:bg-red-900/30 dark:hover:bg-red-900/40',
      textClass: 'text-red-900 dark:text-red-100'
    };
  };

  // Фильтрация данных
  const filteredCouriers = couriersStats.filter(courier => {
    const matchesSearch = courier.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesActivity = activityFilter === 'all' || 
      (activityFilter === 'super' && courier.repeatedPayments >= 5) ||
      (activityFilter === 'high' && courier.repeatedPayments >= 4 && courier.repeatedPayments < 5) ||
      (activityFilter === 'good' && courier.repeatedPayments >= 2 && courier.repeatedPayments < 4) ||
      (activityFilter === 'medium' && courier.repeatedPayments >= 1 && courier.repeatedPayments < 2) ||
      (activityFilter === 'low' && courier.repeatedPayments < 1);
    
    const matchesPayments = paymentsFilter === 'all' ||
      (paymentsFilter === '1-5' && courier.totalPayments >= 1 && courier.totalPayments <= 5) ||
      (paymentsFilter === '6-10' && courier.totalPayments >= 6 && courier.totalPayments <= 10) ||
      (paymentsFilter === '11-20' && courier.totalPayments >= 11 && courier.totalPayments <= 20) ||
      (paymentsFilter === '20+' && courier.totalPayments > 20);
    
    return matchesSearch && matchesActivity && matchesPayments;
  });

  // Функция экспорта в CSV
  const handleExport = () => {
    const csvContent = [
      ['Имя курьера', 'Email', 'Телефон', 'Всего платежей', 'Месяцев с 2+ платежами', 'Месяцев с 3+ платежами', '% активности'].join(','),
      ...filteredCouriers.map(courier => [
        courier.name,
        courier.email || '',
        courier.phone || '',
        courier.totalPayments,
        courier.repeatedPayments,
        courier.multiPayments,
        courier.activityPercentage.toFixed(1)
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `courier_analytics_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Вычисление метрик
  const metrics = {
    totalCouriers: couriersStats.length,
    activeRepeatCouriers: couriersStats.filter(c => c.repeatedPayments > 0).length,
    averageRepeatedPayments: couriersStats.length > 0 
      ? couriersStats.reduce((sum, c) => sum + c.repeatedPayments, 0) / couriersStats.length 
      : 0,
    topCourierPayments: couriersStats.length > 0 
      ? Math.max(...couriersStats.map(c => c.repeatedPayments)) 
      : 0
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>
              <Skeleton className="h-6 w-48" />
            </CardTitle>
          </CardHeader>
          <CardContent className="h-80">
            <Skeleton className="h-full w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-red-500 flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" /> Ошибка при загрузке аналитики курьеров
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p>{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <ConnectionErrorAlert 
        connectionError={connectionError} 
        dataSource={dataSource}
        onRetry={() => window.location.reload()}
      />
      
      <CourierMetricsCard {...metrics} />
      
      <Card>
        <CardHeader>
          <CardTitle>Аналитика активности курьеров</CardTitle>
          <p className="text-sm text-muted-foreground">
            Анализ курьеров по повторным платежам с цветовым кодированием активности
          </p>
        </CardHeader>
        <CardContent>
          <CourierActivityFilters
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            activityFilter={activityFilter}
            setActivityFilter={setActivityFilter}
            paymentsFilter={paymentsFilter}
            setPaymentsFilter={setPaymentsFilter}
            onRefresh={fetchCouriersPaymentStats}
            onExport={handleExport}
            loading={loading}
          />

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Имя курьера</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="text-right">Всего платежей</TableHead>
                <TableHead className="text-center">Активность</TableHead>
                <TableHead className="text-right">Месяцев с 2+ платежами</TableHead>
                <TableHead className="text-right">Месяцев с 3+ платежами</TableHead>
                <TableHead className="text-right">% активности</TableHead>
                <TableHead className="text-center">Статус</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCouriers.length > 0 ? (
                filteredCouriers.map((courier) => {
                  const styles = getRowStyles(courier.repeatedPayments);
                  return (
                    <TableRow 
                      key={courier.id}
                      className={`transition-colors border-b-2 ${styles.bgClass}`}
                    >
                      <TableCell className={`font-semibold ${styles.textClass}`}>
                        {courier.name}
                      </TableCell>
                      <TableCell className={`${styles.textClass}`}>
                        {courier.email || '—'}
                      </TableCell>
                      <TableCell className={`text-right font-bold ${styles.textClass}`}>
                        {courier.totalPayments}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="w-24">
                          <CourierActivityProgressBar 
                            repeatedPayments={courier.repeatedPayments}
                          />
                        </div>
                      </TableCell>
                      <TableCell className={`text-right font-bold text-lg ${styles.textClass}`}>
                        {courier.repeatedPayments}
                      </TableCell>
                      <TableCell className={`text-right font-medium ${styles.textClass}`}>
                        {courier.multiPayments}
                      </TableCell>
                      <TableCell className={`text-right font-bold ${styles.textClass}`}>
                        {courier.activityPercentage.toFixed(1)}%
                      </TableCell>
                      <TableCell className="text-center">
                        <CourierActivityBadge 
                          repeatedPayments={courier.repeatedPayments}
                          multiPayments={courier.multiPayments}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-4 text-muted-foreground">
                    {searchTerm || activityFilter !== 'all' || paymentsFilter !== 'all' 
                      ? 'Нет курьеров, соответствующих фильтрам'
                      : 'Нет данных о повторных платежах курьеров'
                    }
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <div className="mt-4 text-sm text-muted-foreground">
            <p><strong>Уровни активности курьеров:</strong></p>
            <p>• <span className="text-emerald-600 font-semibold">Супер активный</span>: 5+ месяцев с повторными платежами</p>
            <p>• <span className="text-green-600 font-semibold">Высокая активность</span>: 4 месяца с повторными платежами</p>
            <p>• <span className="text-yellow-600 font-semibold">Хорошая активность</span>: 2-3 месяца с повторными платежами</p>
            <p>• <span className="text-orange-600 font-semibold">Средняя активность</span>: 1 месяц с повторными платежами</p>
            <p>• <span className="text-red-600 font-semibold">Низкая активность</span>: 0 месяцев с повторными платежами</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CourierAnalytics;
